import React, { useState, useEffect } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Plus, 
  Trash2, 
  Edit, 
  TestTube, 
  CheckCircle, 
  XCircle,
  Smartphone,
  Shield,
  Zap,
  RefreshCw,
  BarChart3,
  DownloadCloud,
  Save,
  MessageSquare,
  Target,
  Download,
  Key
} from 'lucide-react';

import { Client } from '@/types/client';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { apiService } from '@/services/api'; // Importar o apiService
import { useToast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';
import ApiStrategyModal from './ApiStrategyModal';
import MetaActivationWizard from './MetaActivationWizard';
import AtomosChatApiGuide from './AtomosChatApiGuide';
import WebhooksTab from './WebhooksTab';
import EmailRemetenteSection from './EmailRemetenteSection';

interface WhatsappConnection {
  id?: number;
  client_id: number;
  connection_type: 'oficial' | 'extra_oficial';
  name: string;
  is_active: boolean;
  
  // Campos para monitoramento
  is_online?: boolean;
  connection_status?: 'connected' | 'disconnected' | 'error' | 'unknown';
  last_checked_at?: string;
  
  // Campos Extra Oficial
  chatconnect_account_id?: string;
  
  // Campos Oficial
  whatsapp_number?: string;
  phone_id?: string;
  business_account_id?: string;
  permanent_token?: string;
  chatconnect_oficial_inbox_id?: string;
  
  // Limites de envio
  daily_limit?: number;
  sent_today?: number;
  
  // Configurações de rate limiting
  min_interval_seconds?: number;
  max_interval_seconds?: number;
  delay_seg?: number;
  
  // Janela de tempo para envio
  window_start?: string;
  window_end?: string;
  window_start_formatted?: string | null;
  window_end_formatted?: string | null;
  
  created_at?: string;
  updated_at?: string;
}

interface ApiTabProps {
  client: Client;
}

// Interfaces para respostas da API
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

interface ConnectionResponse {
  connection: WhatsappConnection;
}

interface ConnectionsResponse {
  connections: WhatsappConnection[];
}

interface RateLimitResponse {
  rateLimit: {
    dailyLimit: number;
    currentCount: number;
    remaining: number;
  };
}

interface LimitsResponse {
  limits: Array<{
    connectionId: number;
    dailyLimit: number;
    currentCount: number;
    remaining: number;
    delaySeconds?: number;
  }>;
}

interface DelaysResponse {
  delays: {
    minInterval: number;
    maxInterval: number;
  };
}

interface TemplateCountResponse {
  count: number;
}

/** Config e templates ÁtomosChat (Envios V2) */
interface AtomosChatConfigDto {
  id?: number;
  client_id?: number;
  account_token?: string;
  account_name?: string;
  channel_name?: string | null;
  channel_phone?: string | null;
  channel_id?: string | null;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  auto_close_enabled?: boolean;
  auto_close_delay_minutes?: number;
}
interface AtomosChatTemplateDto {
  id: string;
  name: string;
  text?: string;
  status: string;
  categoryName?: string;
  channelId?: string;
}


const ApiTab: React.FC<ApiTabProps> = ({ client }) => {
  const { toast } = useToast();
  console.log('🎯 [ApiTab] Componente MONTADO - client:', client);
  console.log('🎯 [ApiTab] Componente MONTADO - client.id:', client?.id);
  console.log('🎯 [ApiTab] Componente MONTADO - client.legal_name:', client?.legal_name);
  console.log('🎯 [ApiTab] Componente MONTADO - client.trade_name:', client?.trade_name);
  
  const [connections, setConnections] = useState<WhatsappConnection[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingConnection, setEditingConnection] = useState<WhatsappConnection | null>(null);
  const [testingConnection, setTestingConnection] = useState<number | null>(null);
  const [fetchingTemplatesConnection, setFetchingTemplatesConnection] = useState<number | null>(null);
  const [apiType, setApiType] = useState<'oficial' | 'extra_oficial'>('oficial');
  // Estados para estratégias removidos - agora usando a nova aba Template Strategies
  
  // Estados para Rate Limiting Redis
  const [redisLimits, setRedisLimits] = useState<{ [connectionId: number]: { dailyLimit: number; currentCount: number; remaining: number } }>({});
  const [loadingRedisLimits, setLoadingRedisLimits] = useState(false);
  const [savingRedisLimits, setSavingRedisLimits] = useState(false);
  const [resettingCounter, setResettingCounter] = useState<number | null>(null);
  
  // Estado separado para valores temporários do input (antes de salvar)
  const [tempDailyLimits, setTempDailyLimits] = useState<{ [connectionId: number]: string }>({});
  
  // Estado para controlar persistência visual dos valores salvos
  const [lastSavedLimits, setLastSavedLimits] = useState<{ [connectionId: number]: { value: number; date: string } }>({});

  // Estados para Rate Limiting Meta
  const [metaLimits, setMetaLimits] = useState<{ [connectionId: number]: any }>({});
  const [loadingMetaLimits, setLoadingMetaLimits] = useState(false);
  const [rateLimitStats, setRateLimitStats] = useState<{ [connectionId: number]: any }>({});
  const [loadingRateLimitStats, setLoadingRateLimitStats] = useState(false);
  
  // Estados para configuração de delay
  const [connectionDelays, setConnectionDelays] = useState<{ [connectionId: number]: number }>({});
  const [savingDelay, setSavingDelay] = useState<{ [connectionId: number]: boolean }>({});
  
  // Estados para configuração de limite por conexão
  const [connectionLimits, setConnectionLimits] = useState<{ [connectionId: number]: number }>({});
  const [savingConnectionConfig, setSavingConnectionConfig] = useState<{ [connectionId: number]: boolean }>({});
  
  // Estados para janela de tempo
  const [connectionWindows, setConnectionWindows] = useState<{ [connectionId: number]: { start: string; end: string } }>({});
  const [savingWindow, setSavingWindow] = useState<{ [connectionId: number]: boolean }>({});

  // Estados para modal de estratégias
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [selectedConnectionForStrategy, setSelectedConnectionForStrategy] = useState<WhatsappConnection | null>(null);

  // Estado para modal de ativação da API Meta
  const [showMetaActivationWizard, setShowMetaActivationWizard] = useState(false);

  // Estado para conexão selecionada no dropdown de rate limits
  const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(null);

  // Estados para seletor de conexões e exportação PDF
  const [selectedConnection, setSelectedConnection] = useState<WhatsappConnection | null>(null);
  const [metaTemplates, setMetaTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Envios V2 (ÁtomosChat) — múltiplas conexões por cliente
  const [envioVersion, setEnvioVersion] = useState<'v1' | 'v2' | 'email'>('v1');
  const [atomosChatConfigs, setAtomosChatConfigs] = useState<AtomosChatConfigDto[]>([]);
  const [loadingAtomosChatConfig, setLoadingAtomosChatConfig] = useState(false);
  const [savingAtomosChatConfig, setSavingAtomosChatConfig] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState<number | null>(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [deletingConfigId, setDeletingConfigId] = useState<number | null>(null);
  const [atomosChatConfigForm, setAtomosChatConfigForm] = useState<{
    account_token: string;
    account_name: string;
    channel_name: string;
    channel_phone: string;
    channel_id: string;
    auto_close_enabled: boolean;
    auto_close_delay_minutes: number;
    auto_close_unit: 'minutes' | 'hours';
  }>({
    account_token: '',
    account_name: '',
    channel_name: '',
    channel_phone: '',
    channel_id: '',
    auto_close_enabled: true,
    auto_close_delay_minutes: 0,
    auto_close_unit: 'minutes',
  });
  const [atomosChatTemplates, setAtomosChatTemplates] = useState<AtomosChatTemplateDto[]>([]);
  const [loadingAtomosChatTemplates, setLoadingAtomosChatTemplates] = useState(false);
  const [templatesConfigId, setTemplatesConfigId] = useState<number | null>(null);

  // Estados do formulário
  const [formData, setFormData] = useState<Partial<WhatsappConnection>>({
    connection_type: 'extra_oficial',
    name: '',
    is_active: true
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Carregar conexões existentes
  useEffect(() => {
    console.log('🔄 [ApiTab] useEffect chamado - client:', client);
    console.log('🔄 [ApiTab] useEffect chamado - client.id:', client?.id);
    if (client?.id) {
      console.log('✅ [ApiTab] useEffect - Carregando dados para client.id:', client.id);
      
      // PRIMEIRO: Carregar valores salvos do localStorage (para não perder configurações)
      console.log('💾 [ApiTab] Carregando configurações do localStorage...');
      loadPersistedLimits();
      loadPersistedConfig();
      
      // SEGUNDO: Carregar conexões
      console.log('🔄 [ApiTab] Carregando conexões...');
      loadConnections();
      
      // TERCEIRO: Tentar carregar dados do backend (pode falhar por auth)
      console.log('🔄 [ApiTab] Tentando carregar limites Redis do backend...');
      loadRedisLimits();
      
      // Carregar dados de rate limit apenas se o usuário estiver autenticado
      if (localStorage.getItem('token')) {
        console.log('🔄 [ApiTab] Usuário autenticado - carregando Meta limits e stats...');
        loadMetaLimits();
        loadRateLimitStats();
      } else {
        console.log('⚠️ [ApiTab] Token não encontrado no localStorage');
      }
    } else {
      console.log('❌ [ApiTab] useEffect - client.id não encontrado');
    }
  }, [client?.id]);

  // Selecionar automaticamente a primeira conexão quando as conexões carregarem
  useEffect(() => {
    if (connections.length > 0 && !selectedConnectionId) {
      const firstConnection = connections.find(c => c.connection_type === apiType);
      if (firstConnection) {
        setSelectedConnectionId(firstConnection.id!);
        console.log('🔄 [ApiTab] Selecionando automaticamente a primeira conexão:', firstConnection.name);
      }
    }
  }, [connections, apiType, selectedConnectionId]);

  // Carregar templates quando uma conexão for selecionada
  useEffect(() => {
    if (selectedConnection && selectedConnection.connection_type === 'oficial') {
      loadMetaTemplates(selectedConnection.id!);
    }
  }, [selectedConnection]);

  // Carregar configs ÁtomosChat (Envios V2) ao montar
  useEffect(() => {
    if (client?.id) {
      loadAtomosChatConfigs();
    }
  }, [client?.id]);

  const resetConfigForm = () => {
    setAtomosChatConfigForm({
      account_token: '',
      account_name: '',
      channel_name: '',
      channel_phone: '',
      channel_id: '',
      auto_close_enabled: true,
      auto_close_delay_minutes: 0,
      auto_close_unit: 'minutes',
    });
  };

  // Função para carregar lista de configs e a preferência V1/V2 do cliente
  const loadAtomosChatConfigs = async () => {
    if (!client?.id) return;
    try {
      setLoadingAtomosChatConfig(true);
      const [configsRes, versionRes] = await Promise.all([
        apiService.get(`/atomos-chat/client/${client.id}/configs`) as Promise<{ success: boolean; configs: AtomosChatConfigDto[] }>,
        apiService.get(`/atomos-chat/client/${client.id}/envio-version`) as Promise<{ success: boolean; envio_version: 'v1' | 'v2' | 'email' }>,
      ]);
      const configs = configsRes.configs ?? [];
      setAtomosChatConfigs(configs);
      setEnvioVersion(versionRes.envio_version ?? 'v1');
      // Se não está editando, mostra o form só quando lista vazia
      setShowConfigForm((prev) => (editingConfigId != null ? prev : configs.length === 0));
      const firstActive = configs.find((c) => c.is_active !== false);
      if (firstActive && templatesConfigId == null) {
        setTemplatesConfigId(firstActive.id ?? null);
      }
    } catch (e) {
      console.error('[ApiTab] loadAtomosChatConfigs:', e);
      setAtomosChatConfigs([]);
      setEnvioVersion('v1');
    } finally {
      setLoadingAtomosChatConfig(false);
    }
  };

  const handleEnvioVersionChange = async (value: 'v1' | 'v2' | 'email') => {
    if (value === envioVersion) return;
    try {
      await apiService.put(`/atomos-chat/client/${client!.id}/envio-version`, { envio_version: value });
      setEnvioVersion(value);
      if (value === 'v2') {
        // Ao voltar para V2, garante que se não há configs, o form aparece
        setShowConfigForm(atomosChatConfigs.length === 0);
      } else {
        // V1 ou Email: não usa o form/templates de V2
        setAtomosChatTemplates([]);
        setEditingConfigId(null);
        setShowConfigForm(false);
        resetConfigForm();
      }
    } catch (e) {
      console.error('[ApiTab] handleEnvioVersionChange:', e);
      toast({ title: 'Erro ao alterar versão', description: 'Não foi possível salvar a preferência.', variant: 'destructive' });
    }
  };

  const startNewConfig = () => {
    setEditingConfigId(null);
    resetConfigForm();
    setShowConfigForm(true);
    setErrors(prev => ({ ...prev, atomosChat: '' }));
  };

  const editAtomosChatConfig = (config: AtomosChatConfigDto) => {
    setEditingConfigId(config.id ?? null);
    setAtomosChatConfigForm({
      account_token: '', // nunca preencher por segurança
      account_name: config.account_name ?? '',
      channel_name: config.channel_name ?? '',
      channel_phone: config.channel_phone ?? '',
      channel_id: config.channel_id ?? '',
      auto_close_enabled: config.auto_close_enabled ?? true,
      auto_close_delay_minutes: config.auto_close_delay_minutes ?? 0,
      auto_close_unit:
        (config.auto_close_delay_minutes ?? 0) >= 60 &&
        (config.auto_close_delay_minutes ?? 0) % 60 === 0
          ? 'hours'
          : 'minutes',
    });
    setShowConfigForm(true);
    setErrors(prev => ({ ...prev, atomosChat: '' }));
  };

  const cancelConfigEdit = () => {
    setEditingConfigId(null);
    resetConfigForm();
    setShowConfigForm(atomosChatConfigs.length === 0);
    setErrors(prev => ({ ...prev, atomosChat: '' }));
  };

  const reactivateAtomosChatConfig = async (configId: number) => {
    if (!client?.id || !configId) return;
    try {
      setSavingAtomosChatConfig(true);
      await apiService.put(`/atomos-chat/client/${client.id}/configs/${configId}`, { is_active: true });
      await loadAtomosChatConfigs();
      toast({ title: 'Conexão reativada' });
    } catch (e: any) {
      console.error('[ApiTab] reactivateAtomosChatConfig:', e);
      toast({ title: 'Erro ao reativar', description: e?.message || 'Erro interno', variant: 'destructive' });
    } finally {
      setSavingAtomosChatConfig(false);
    }
  };

  const deleteAtomosChatConfig = async (configId: number) => {
    if (!client?.id || !configId) return;
    if (!window.confirm('Remover esta conexão V2? Estratégias que apontavam para ela cairão no fallback (primeira ativa).')) return;
    try {
      setDeletingConfigId(configId);
      await apiService.delete(`/atomos-chat/client/${client.id}/configs/${configId}`);
      if (editingConfigId === configId) cancelConfigEdit();
      if (templatesConfigId === configId) setTemplatesConfigId(null);
      await loadAtomosChatConfigs();
      toast({ title: 'Conexão removida' });
    } catch (e: any) {
      console.error('[ApiTab] deleteAtomosChatConfig:', e);
      toast({ title: 'Erro ao remover', description: e?.message || 'Erro interno', variant: 'destructive' });
    } finally {
      setDeletingConfigId(null);
    }
  };

  const saveAtomosChatConfig = async () => {
    if (!client?.id) return;
    const isCreate = editingConfigId == null;
    if (isCreate && !atomosChatConfigForm.account_token.trim()) {
      setErrors(prev => ({ ...prev, atomosChat: 'Informe o token da conta para criar a conexão.' }));
      return;
    }
    if (!atomosChatConfigForm.account_name.trim()) {
      setErrors(prev => ({ ...prev, atomosChat: 'Informe o nome do cliente na plataforma.' }));
      return;
    }
    if (atomosChatConfigForm.auto_close_enabled) {
      const totalMin = atomosChatConfigForm.auto_close_unit === 'hours'
        ? atomosChatConfigForm.auto_close_delay_minutes * 60
        : atomosChatConfigForm.auto_close_delay_minutes;
      if (totalMin < 0 || totalMin > 4320) {
        setErrors(prev => ({ ...prev, atomosChat: 'O tempo de fechamento deve ser entre 0 e 72 horas.' }));
        return;
      }
    }
    try {
      setSavingAtomosChatConfig(true);
      setErrors(prev => ({ ...prev, atomosChat: '' }));
      const payload: Record<string, string | boolean | number> = {
        account_name: atomosChatConfigForm.account_name.trim(),
      };
      if (atomosChatConfigForm.account_token.trim()) payload.account_token = atomosChatConfigForm.account_token.trim();
      if (atomosChatConfigForm.channel_name.trim()) payload.channel_name = atomosChatConfigForm.channel_name.trim();
      if (atomosChatConfigForm.channel_phone.trim()) payload.channel_phone = atomosChatConfigForm.channel_phone.trim();
      if (atomosChatConfigForm.channel_id.trim()) payload.channel_id = atomosChatConfigForm.channel_id.trim();
      payload.auto_close_enabled = atomosChatConfigForm.auto_close_enabled;
      payload.auto_close_delay_minutes = atomosChatConfigForm.auto_close_enabled
        ? (atomosChatConfigForm.auto_close_unit === 'hours'
            ? atomosChatConfigForm.auto_close_delay_minutes * 60
            : atomosChatConfigForm.auto_close_delay_minutes)
        : 0;
      if (isCreate) {
        await apiService.post(`/atomos-chat/client/${client.id}/configs`, payload);
      } else {
        await apiService.put(`/atomos-chat/client/${client.id}/configs/${editingConfigId}`, payload);
      }
      setEditingConfigId(null);
      resetConfigForm();
      await loadAtomosChatConfigs();
      setShowConfigForm(false);
      toast({
        title: isCreate ? 'Conexão criada' : 'Conexão atualizada',
        description: 'Configuração ÁtomosChat salva com sucesso.',
      });
    } catch (e: any) {
      console.error('[ApiTab] saveAtomosChatConfig:', e);
      const msg = e?.message ?? 'Erro ao salvar';
      setErrors(prev => ({ ...prev, atomosChat: msg }));
      toast({
        title: 'Erro ao salvar',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setSavingAtomosChatConfig(false);
    }
  };

  const loadAtomosChatTemplates = async () => {
    if (!client?.id) return;
    const TIMEOUT_MS = 35000; // 35s (backend tem 30s para a API externa)
    try {
      setLoadingAtomosChatTemplates(true);
      setErrors(prev => ({ ...prev, atomosChat: '' }));
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Tempo esgotado. A requisição demorou mais de 35 segundos. Verifique sua conexão ou se a API ÁtomosChat está acessível.')), TIMEOUT_MS)
      );
      const url = templatesConfigId
        ? `/atomos-chat/client/${client.id}/templates?config_id=${templatesConfigId}`
        : `/atomos-chat/client/${client.id}/templates`;
      const data = await Promise.race([
        apiService.get(url) as Promise<{ success: boolean; templates: AtomosChatTemplateDto[]; count: number }>,
        timeoutPromise,
      ]);
      setAtomosChatTemplates(data.templates ?? []);
      setErrors(prev => ({ ...prev, atomosChat: '' }));
      toast({
        title: 'Templates carregados',
        description: `${data.templates?.length ?? 0} template(s) da ÁtomosChat.`,
      });
    } catch (e: any) {
      console.error('[ApiTab] loadAtomosChatTemplates:', e);
      const msg = e?.message ?? 'Configure o token e salve antes de carregar os templates.';
      const lower = msg.toLowerCase();
      const is401 = lower.includes('401') || lower.includes('não autenticado') || lower.includes('não autorizado') || lower.includes('unauthorized');
      const isAtomosTokenError = lower.includes('token inválido') || lower.includes('token da conta') || lower.includes('expirado');
      const isTimeout = lower.includes('tempo esgotado') || lower.includes('timeout');
      const friendlyMsg = isTimeout
        ? msg
        : is401
          ? (isAtomosTokenError
            ? 'Token da conta ÁtomosChat inválido ou expirado. Atualize o token na configuração acima e salve novamente.'
            : 'Não autorizado. Faça login novamente no Portal ou verifique o token da conta ÁtomosChat.')
          : msg;
      setErrors(prev => ({ ...prev, atomosChat: friendlyMsg }));
      setAtomosChatTemplates([]);
      toast({
        title: 'Erro ao carregar templates',
        description: friendlyMsg,
        variant: 'destructive',
      });
    } finally {
      setLoadingAtomosChatTemplates(false);
    }
  };

  // Função para carregar valores persistentes do localStorage
  const loadPersistedLimits = () => {
    try {
      const savedLimits = localStorage.getItem(`rate_limits_${client?.id}`);
      if (savedLimits) {
        const parsed = JSON.parse(savedLimits);
        const today = new Date().toDateString();
        
        // Filtrar apenas valores que são do dia atual
        const validLimits: { [connectionId: number]: { value: number; date: string } } = {};
        Object.entries(parsed).forEach(([connectionId, data]: [string, any]) => {
          if (data.date === today) {
            validLimits[parseInt(connectionId)] = data;
          }
        });
        
        setLastSavedLimits(validLimits);
        console.log('💾 [ApiTab] Valores persistentes carregados:', validLimits);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar valores persistentes:', error);
    }
  };

  // Função para carregar configurações salvas do localStorage
  const loadPersistedConfig = () => {
    try {
      const configKey = `rate_limit_config_${client?.id}`;
      console.log('💾 [ApiTab] Tentando carregar configurações com chave:', configKey);
      
      const savedConfig = localStorage.getItem(configKey);
      console.log('💾 [ApiTab] Dados brutos do localStorage:', savedConfig);
      
      if (savedConfig) {
        const parsed = JSON.parse(savedConfig);
        console.log('💾 [ApiTab] Configurações parseadas:', parsed);
        
        // Restaurar valores nos estados
        const limits: { [connectionId: number]: number } = {};
        const delays: { [connectionId: number]: number } = {};
        
        Object.entries(parsed).forEach(([connectionId, config]: [string, any]) => {
          console.log('💾 [ApiTab] Processando config para conexão:', connectionId, config);
          if (config.dailyLimit !== undefined) {
            limits[parseInt(connectionId)] = config.dailyLimit;
          }
          if (config.delaySeconds !== undefined) {
            delays[parseInt(connectionId)] = config.delaySeconds;
          }
        });
        
        console.log('💾 [ApiTab] Aplicando estados:', { limits, delays });
        setConnectionLimits(limits);
        setConnectionDelays(delays);
        console.log('✅ [ApiTab] Estados restaurados do localStorage com sucesso');
      } else {
        console.log('💾 [ApiTab] Nenhuma configuração encontrada no localStorage');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar configurações do localStorage:', error);
    }
  };


  const loadConnections = async () => {
    console.log('🚀 [ApiTab] loadConnections INICIADA - client:', client);
    if (!client?.id) {
      console.log('❌ [ApiTab] loadConnections - client.id não encontrado');
      return;
    }

    console.log('✅ [ApiTab] loadConnections - Iniciando carregamento para client.id:', client.id);
    setLoading(true);
    const timeoutMs = 15000;
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setConnections([]);
      console.warn('[ApiTab] loadConnections timeout - exibindo aba sem travar');
    }, timeoutMs);
    try {
      // 1) Endpoint preferencial por clientId
      let data: any = await apiService.get(`/whatsapp/client/${client.id}`);

      // 2) Normalização da resposta
      const normalize = (payload: any) => {
        if (!payload) return [] as any[];
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload.connections)) return payload.connections;
        if (payload.data && Array.isArray(payload.data.connections)) return payload.data.connections;
        if (payload.success && Array.isArray(payload.data)) return payload.data;
        return [] as any[];
      };

      let resolvedConnections = normalize(data);

      // 3) Garantir que todas as conexões pertencem ao cliente atual
      // Filtrar apenas conexões do cliente específico para evitar mostrar conexões de outros clientes
      resolvedConnections = resolvedConnections.filter((conn: any) =>
        conn.client_id === client.id
      );

      // 4) NÃO fazer fallback para /whatsapp/client/current se não houver conexões
      // Isso evita mostrar conexões de outros clientes quando o cliente atual não tem nenhuma
      if (resolvedConnections.length === 0) {
        console.log('ℹ️ [ApiTab] Nenhuma conexão encontrada para o cliente', client.id);
      }

      console.log('CONEXÕES FINAIS (filtradas por client_id):', resolvedConnections);
      console.log('TIPOS DE CONEXÃO:', resolvedConnections.map((c: any) => ({ id: c.id, name: c.name, type: c.connection_type, client_id: c.client_id })));
      setConnections(resolvedConnections as any);
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
      // Em caso de erro, definir conexões vazias para não mostrar dados incorretos
      setConnections([]);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const loadConnectChatConnections = async () => {
    if (!client?.id) {
      alert('Cliente não identificado');
      return;
    }

    if (!client.account_id) {
      alert('Cliente não possui Account ID do ConnectChat configurado. Configure nas configurações do cliente.');
      return;
    }

    setLoading(true);
    try {
      // Primeiro, buscar conexões atualizadas do banco de dados
      console.log('🔄 Carregando conexões atuais do banco...');
      const currentData = await apiService.get(`/whatsapp/client/${client.id}`) as ApiResponse<ConnectionsResponse>;

      let currentConnections: WhatsappConnection[] = currentData.data?.connections || [];
        console.log(`📋 ${currentConnections.length} conexões encontradas no banco`);

      const data = await apiService.get(`/connectchat/connections/client/${client.id}`) as any;
        
        // O backend retorna um objeto com connections
        if (Array.isArray(data.connections) && data.connections.length > 0) {
          console.log(`🔍 Encontradas ${data.connections.length} conexões ConnectChat:`, data.connections);
          
          // Todas as conexões do ConnectChat são válidas (têm identifier)
          const validConnections = data.connections.filter((conn: any) => conn.identifier && conn.identifier.trim() !== '') || [];

          // Remover duplicatas pelo identifier (ignorando espaços e diferenças de tipo)
          const uniqueConnectionsMap = new Map<string, any>();
          validConnections.forEach((conn: any) => {
            const key = String(conn.identifier).trim();
            if (!uniqueConnectionsMap.has(key)) {
              uniqueConnectionsMap.set(key, conn);
            }
          });
          const uniqueConnections = Array.from(uniqueConnectionsMap.values());

          if (uniqueConnections.length === 0) {
            alert('Nenhuma conexão válida encontrada no ConnectChat');
            return;
          }

          // Verificar quais conexões já existem no banco (usando dados atualizados)
          const existingConnectionIds = currentConnections
            .filter(conn => conn.chatconnect_account_id)
            .map(conn => conn.chatconnect_account_id!.trim());

          console.log(`📋 Conexões já existentes no banco:`, existingConnectionIds);

          // Filtrar apenas conexões que ainda não existem
          const newConnections = uniqueConnections.filter((conn: any) => 
            !existingConnectionIds.includes(String(conn.identifier).trim())
          );

          console.log(`🔍 Conexões novas para importar: ${newConnections.length} de ${uniqueConnections.length}`);

          if (newConnections.length === 0) {
            alert(`Todas as ${uniqueConnections.length} conexões do ConnectChat já estão sincronizadas!\n\nNenhuma nova conexão para importar.`);
            // Atualizar estado local com dados do banco
            setConnections(currentConnections);
            return;
          }

          // Confirmar importação com o usuário
          const confirmMessage = `Encontradas ${uniqueConnections.length} conexões no ConnectChat.\n\n` +
            `• ${existingConnectionIds.length} já sincronizadas\n` +
            `• ${newConnections.length} novas para importar\n\n` +
            `Deseja importar as ${newConnections.length} novas conexões?`;

          if (!confirm(confirmMessage)) {
            // Atualizar estado local mesmo se não importar
            setConnections(currentConnections);
            return;
          }

          // Importar apenas as conexões novas
          let imported = 0;
          let errors = 0;
          let duplicates = 0;

          for (const conn of newConnections) {
            try {
              console.log(`📥 Importando: ${conn.name} (${conn.identifier})`);
              
            await apiService.post('/whatsapp/create', {
                  client_id: client.id,
                  connection_type: 'extra_oficial',
                  name: conn.name,
                  chatconnect_account_id: conn.identifier,
                  is_active: true
              });

                imported++;
                console.log(`✅ Conexão importada: ${conn.name} (${conn.identifier})`);
          } catch (error: any) {
            console.error(`❌ Erro ao importar ${conn.name}:`, error.message);
                
                // Se o erro for de duplicata, não contar como erro
            if (error.message && error.message.includes('já existe')) {
                  console.log(`⚠️ Conexão ${conn.name} já existe (detectada pelo backend)`);
                  duplicates++;
                } else {
                  errors++;
                }
            }

            // Pequena pausa entre importações
            await new Promise(resolve => setTimeout(resolve, 500));
          }

          // Recarregar conexões para mostrar as novas
          await loadConnections();

          // Mostrar resultado detalhado
          let resultMessage = `Importação concluída!\n\n`;
          
          if (imported > 0) {
            resultMessage += `✅ ${imported} novas conexões importadas com sucesso!\n`;
          }
          
          if (duplicates > 0) {
            resultMessage += `⚠️ ${duplicates} conexões já existiam (ignoradas)\n`;
          }
          
          if (errors > 0) {
            resultMessage += `❌ ${errors} conexões com erro\n`;
          }
          
          if (existingConnectionIds.length > 0) {
            resultMessage += `📋 ${existingConnectionIds.length} conexões já sincronizadas anteriormente\n`;
          }

          resultMessage += `\n📊 Total de conexões agora: ${currentConnections.length + imported}`;

          alert(resultMessage);

        } else {
          alert('Nenhuma conexão encontrada no ConnectChat para este cliente');
        }
    } catch (error: any) {
          console.error('Erro ao carregar conexões do ConnectChat:', error);
      alert(`Erro ao carregar conexões do ConnectChat: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };


  const loadUsages = async (connectionId:number)=>{
    try{
      const data = await apiService.get(`/whatsapp/${connectionId}/template-usages`);
    }catch(err){console.error('Erro ao carregar dados da conexão',err);}  
  };

  // Funções para Rate Limiting Redis
  const loadRedisLimits = async () => {
    if (!client?.id) return;
    
    console.log('🔄 [ApiTab] Carregando limites Redis para cliente:', client.id);
    console.log('🔄 [ApiTab] Cliente details:', {
      id: client.id,
      legal_name: client.legal_name,
      trade_name: client.trade_name
    });
    console.log('🔄 [ApiTab] URL que será chamada:', `/rate-limits/redis/client/${client.id}`);
    setLoadingRedisLimits(true);
    try {
      const data = await apiService.get(`/rate-limits/redis/client/${client.id}`) as ApiResponse & LimitsResponse;
      console.log('📊 [ApiTab] Resposta completa dos limites Redis:', data);
      console.log('📊 [ApiTab] Status da resposta:', data.success);
      console.log('📊 [ApiTab] Dados recebidos:', data.data);
      
      if (data.success && data.limits && Array.isArray(data.limits)) {
        const limitsMap: { [connectionId: number]: { dailyLimit: number; currentCount: number; remaining: number } } = {};
        data.limits.forEach((limit: any) => {
          console.log('🏷️ [ApiTab] Processando limite para conexão:', limit.connectionId, {
            dailyLimit: limit.dailyLimit,
            currentCount: limit.currentCount,
            remaining: limit.remaining,
            delaySeconds: limit.delaySeconds
          });
          limitsMap[limit.connectionId] = {
            dailyLimit: limit.dailyLimit || 0,
            currentCount: limit.currentCount || 0,
            remaining: limit.remaining || 0
          };
        });
        console.log('💾 [ApiTab] Limites Redis processados:', limitsMap);
        setRedisLimits(limitsMap);
        
        // Se temos dados do Redis, atualizar também os estados locais (mas não sobrescrever se já configurado)
        data.limits.forEach((limit: any) => {
          if (limit.dailyLimit > 0) {
            setConnectionLimits(prev => ({ 
              ...prev, 
              [limit.connectionId]: prev[limit.connectionId] || limit.dailyLimit 
            }));
          }
          if (limit.delaySeconds > 0) {
            setConnectionDelays(prev => ({ 
              ...prev, 
              [limit.connectionId]: prev[limit.connectionId] || limit.delaySeconds 
            }));
          }
        });
        
      } else {
        console.log('⚠️ [ApiTab] Dados de limites Redis inválidos ou vazios:', {
          success: data.success,
          hasData: !!data.data,
          hasLimits: !!data.data?.limits,
          isArray: Array.isArray(data.data?.limits)
        });
      }
    } catch (error: any) {
      console.error('❌ Erro ao carregar limites Redis:', error);
      if (error.message?.includes('401') || error.message?.includes('403')) {
        console.error('🔐 [ApiTab] Erro de autenticação - token pode estar expirado');
      }
    } finally {
      setLoadingRedisLimits(false);
    }
  };

  // Funções para Rate Limiting Meta
  const loadMetaLimits = async () => {
    if (!client?.id) return;
    
    setLoadingMetaLimits(true);
    try {
      const limitsMap: { [connectionId: number]: any } = {};
      
      // Carregar limites da Meta para cada conexão
      for (const connection of connections) {
        try {
          const data = await apiService.get(`/rate-limit/connection/${connection.id}/meta-limits`) as ApiResponse;
          if (data.success && data.data?.metaLimits) {
            limitsMap[connection.id!] = data.data.metaLimits;
          }
        } catch (error) {
          console.error(`Erro ao carregar limites Meta para conexão ${connection.id}:`, error);
        }
      }
      
      setMetaLimits(limitsMap);
    } catch (error) {
      console.error('Erro ao carregar limites Meta:', error);
    } finally {
      setLoadingMetaLimits(false);
    }
  };

  const loadRateLimitStats = async () => {
    if (!client?.id) return;
    
    setLoadingRateLimitStats(true);
    try {
      const statsMap: { [connectionId: number]: any } = {};
      
      // Carregar estatísticas de rate limiting para cada conexão
      for (const connection of connections) {
        try {
          const data = await apiService.get(`/rate-limit/connection/${connection.id}/stats`) as ApiResponse;
          if (data.success && data.data?.rateLimit) {
            statsMap[connection.id!] = data.data.rateLimit;
          }
        } catch (error) {
          console.error(`Erro ao carregar stats de rate limiting para conexão ${connection.id}:`, error);
        }
      }
      
      setRateLimitStats(statsMap);
    } catch (error) {
      console.error('Erro ao carregar estatísticas de rate limiting:', error);
    } finally {
      setLoadingRateLimitStats(false);
    }
  };

  // Nova função para salvar configurações completas da conexão
  const saveConnectionConfig = async (connectionId: number, dailyLimit: number, delaySeconds: number): Promise<boolean> => {
    console.log('🔧 [ApiTab] Salvando configuração completa para conexão:', connectionId, { dailyLimit, delaySeconds });
    setSavingConnectionConfig(prev => ({ ...prev, [connectionId]: true }));
    
    try {
      const response = await apiService.post('/rate-limits/redis/configure', {
        connectionId,
        dailyLimit,
        delaySeconds
      }) as ApiResponse;
      
      console.log('📊 [ApiTab] Resposta da configuração:', response);
      
      if (response.success) {
        console.log('✅ [ApiTab] Configuração salva com sucesso');
        
        // Atualizar estados locais imediatamente
        setRedisLimits(prev => ({
          ...prev,
          [connectionId]: {
            dailyLimit: dailyLimit,
            currentCount: prev[connectionId]?.currentCount || 0,
            remaining: dailyLimit - (prev[connectionId]?.currentCount || 0)
          }
        }));
        
        // Persistir valores configurados para não perder ao trocar de aba
        setConnectionLimits(prev => ({ ...prev, [connectionId]: dailyLimit }));
        setConnectionDelays(prev => ({ ...prev, [connectionId]: delaySeconds }));
        
        // Salvar no localStorage para persistência entre sessões
        try {
          const configKey = `rate_limit_config_${client?.id}`;
          const currentConfig = localStorage.getItem(configKey);
          const config = currentConfig ? JSON.parse(currentConfig) : {};
          config[connectionId] = {
            dailyLimit,
            delaySeconds,
            savedAt: new Date().toISOString()
          };
          localStorage.setItem(configKey, JSON.stringify(config));
          console.log('💾 [ApiTab] Configuração salva no localStorage:', config);
        } catch (storageError) {
          console.warn('⚠️ [ApiTab] Erro ao salvar no localStorage:', storageError);
        }
        
        // Feedback visual melhorado
        alert(`✅ Configuração salva com sucesso!\n\n• Limite diário: ${dailyLimit} msgs/dia\n• Delay entre mensagens: ${delaySeconds}s\n• Conexão: ${connections.find(c => c.id === connectionId)?.name}`);
        
        // Recarregar dados para confirmar
        setTimeout(() => {
          loadRedisLimits();
        }, 1000);
        
        return true;
      } else {
        throw new Error(response.error || 'Erro desconhecido');
      }
    } catch (error: any) {
      console.error('❌ Erro ao salvar configuração:', error);
      alert(`❌ Erro ao salvar configuração: ${error.message}\n\nVerifique:\n• Se você está logado\n• Se a conexão está ativa\n• Se os valores são válidos`);
      return false;
    } finally {
      setSavingConnectionConfig(prev => ({ ...prev, [connectionId]: false }));
    }
  };

  // Função para salvar janela de tempo de uma conexão
  const saveConnectionWindow = async (connectionId: number, windowStart: string, windowEnd: string): Promise<boolean> => {
    console.log('🕐 [ApiTab] Salvando janela de tempo para conexão:', connectionId, { windowStart, windowEnd });
    setSavingWindow(prev => ({ ...prev, [connectionId]: true }));
    
    try {
      // Validar horários
      const startTime = new Date(`2000-01-01T${windowStart}:00`);
      const endTime = new Date(`2000-01-01T${windowEnd}:00`);
      const minTime = new Date(`2000-01-01T00:00:00`);
      const maxTime = new Date(`2000-01-01T23:59:00`);
      
      if (startTime < minTime || startTime > maxTime) {
        throw new Error('Horário de início deve estar entre 00:00 e 23:59');
      }
      
      if (endTime < minTime || endTime > maxTime) {
        throw new Error('Horário de fim deve estar entre 00:00 e 23:59');
      }
      
      if (startTime >= endTime) {
        throw new Error('Horário de início deve ser anterior ao horário de fim');
      }
      
      console.log('🍪 [ApiTab] Cookies antes da requisição:', document.cookie);
      
      const response = await apiService.put(`/whatsapp/${connectionId}/window`, {
        window_start: windowStart,
        window_end: windowEnd
      }) as ApiResponse;
      
      console.log('📊 [ApiTab] Resposta da janela de tempo:', response);
      
      if (response.success) {
        console.log('✅ [ApiTab] Janela de tempo salva com sucesso');
        
        // Atualizar estado local
        setConnectionWindows(prev => ({
          ...prev,
          [connectionId]: { start: windowStart, end: windowEnd }
        }));
        
        // Feedback visual
        alert(`✅ Janela de tempo salva com sucesso!\n\n• Início: ${windowStart}\n• Fim: ${windowEnd}\n• Conexão: ${connections.find(c => c.id === connectionId)?.name}`);
        
        return true;
      } else {
        throw new Error(response.error || 'Erro desconhecido');
      }
    } catch (error: any) {
      console.error('❌ Erro ao salvar janela de tempo:', error);
      alert(`❌ Erro ao salvar janela de tempo: ${error.message}\n\nVerifique:\n• Se os horários estão entre 00:00 e 23:59\n• Se o horário de início é anterior ao fim\n• Se você está logado`);
      return false;
    } finally {
      setSavingWindow(prev => ({ ...prev, [connectionId]: false }));
    }
  };

  // Função para salvar delay de uma conexão
  const saveConnectionDelay = async (connectionId: number, delaySeconds: number) => {
    console.log('🔧 [ApiTab] Salvando delay para conexão:', connectionId, 'valor:', delaySeconds);
    setSavingDelay(prev => ({ ...prev, [connectionId]: true }));
    
    try {
      console.log('📡 [ApiTab] Fazendo requisição PUT para:', `/rate-limit/connection/${connectionId}/config`);
      console.log('📦 [ApiTab] Dados enviados:', { min_interval_seconds: delaySeconds });
      
      const response = await apiService.put(`/rate-limit/connection/${connectionId}/config`, {
        min_interval_seconds: delaySeconds
      }) as ApiResponse;
      
      console.log('📊 [ApiTab] Resposta recebida:', response);
      
      if (response.success) {
        console.log('✅ [ApiTab] Salvamento bem-sucedido');
        
        // Atualizar o estado local
        setConnectionDelays(prev => ({ ...prev, [connectionId]: delaySeconds }));
        
        // Atualizar a conexão na lista
        setConnections(prev => prev.map(conn => 
          conn.id === connectionId 
            ? { ...conn, min_interval_seconds: delaySeconds }
            : conn
        ));
        
        alert(`✅ Delay configurado com sucesso: ${delaySeconds}s`);
        return true;
      } else {
        throw new Error(response.message || 'Erro ao salvar delay');
      }
    } catch (error: any) {
      console.error('❌ Erro ao salvar delay:', error);
      alert(`❌ Erro ao salvar delay: ${error.message}`);
      return false;
    } finally {
      setSavingDelay(prev => ({ ...prev, [connectionId]: false }));
    }
  };

  const saveRedisLimit = async (connectionId: number, dailyLimit: number) => {
    console.log('🔧 [ApiTab] Salvando limite Redis para conexão:', connectionId, 'limite:', dailyLimit);
    setSavingRedisLimits(true);
    try {
      console.log('📡 [ApiTab] Fazendo requisição POST para:', '/rate-limits/redis/configure');
      console.log('📦 [ApiTab] Dados enviados:', { connectionId, dailyLimit });
      
      const response = await apiService.post('/rate-limits/redis/configure', {
        connectionId,
        dailyLimit
      }) as ApiResponse;
      
      console.log('📊 [ApiTab] Resposta do Redis:', response);
      
      if (response.success) {
        console.log('✅ [ApiTab] Limite Redis salvo com sucesso');
        
        // Atualizar estado local imediatamente
        setRedisLimits(prev => ({
          ...prev,
          [connectionId]: {
            ...prev[connectionId],
            dailyLimit: dailyLimit,
            remaining: dailyLimit - (prev[connectionId]?.currentCount || 0)
          }
        }));
        
        // Salvar valor persistente com data atual
        const today = new Date().toDateString();
        const newPersistedLimit = { value: dailyLimit, date: today };
        
        setLastSavedLimits(prev => ({
          ...prev,
          [connectionId]: newPersistedLimit
        }));
        
        // Salvar no localStorage para persistência entre sessões
        try {
          const currentSaved = localStorage.getItem(`rate_limits_${client?.id}`) || '{}';
          const parsed = JSON.parse(currentSaved);
          parsed[connectionId] = newPersistedLimit;
          localStorage.setItem(`rate_limits_${client?.id}`, JSON.stringify(parsed));
          console.log('💾 [ApiTab] Valor salvo no localStorage:', { connectionId, value: dailyLimit, date: today });
        } catch (storageError) {
          console.warn('⚠️ [ApiTab] Erro ao salvar no localStorage:', storageError);
        }
        
        // Limpar valor temporário do input após salvamento
        setTempDailyLimits(prev => {
          const newState = { ...prev };
          delete newState[connectionId];
          return newState;
        });
        
        // Mostrar mensagem de sucesso
        alert(`✅ Limite diário configurado com sucesso: ${dailyLimit} mensagens/dia`);
        
        // Recarregar limites para garantir sincronização
        setTimeout(() => {
          console.log('🔄 [ApiTab] Recarregando limites Redis após salvamento');
          loadRedisLimits();
        }, 500);
        
        return true;
      } else {
        throw new Error(response.message || 'Erro ao salvar limite');
      }
    } catch (error: any) {
      console.error('❌ [ApiTab] Erro ao salvar limite Redis:', error);
      
      // Mostrar mensagem de erro detalhada
      const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido ao salvar limite';
      alert(`❌ Erro ao configurar limite diário: ${errorMessage}`);
      
      return false;
    } finally {
      setSavingRedisLimits(false);
    }
  };

  const resetConnectionCounter = async (connectionId: number) => {
    if (!confirm('Tem certeza que deseja resetar o contador diário desta conexão?')) {
      return;
    }

    setResettingCounter(connectionId);
    try {
      const response = await apiService.post(`/rate-limits/redis/reset-counter/${connectionId}`) as ApiResponse;
      
      if (response.success) {
        // Atualizar o estado local
        setRedisLimits(prev => ({
          ...prev,
          [connectionId]: {
            ...prev[connectionId],
            currentCount: 0,
            remaining: prev[connectionId]?.dailyLimit || 0
          }
        }));
        
        alert(`✅ Contador resetado com sucesso para conexão ${connectionId}`);
      } else {
        throw new Error(response.error || 'Erro ao resetar contador');
      }
    } catch (error: any) {
      console.error('Erro ao resetar contador:', error);
      alert(`Erro ao resetar contador: ${error.message}`);
    } finally {
      setResettingCounter(null);
    }
  };


  // Carregamento básico para conexões oficiais
  useEffect(()=>{
    if(editingConnection && editingConnection.connection_type==='oficial'){
      // Carregar dados específicos se necessário
    }
  },[editingConnection]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpar erro do campo se existir
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };


  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (formData.connection_type === 'extra_oficial') {
      if (!formData.chatconnect_account_id?.trim()) {
        newErrors.chatconnect_account_id = 'ID da Conexão é obrigatório';
      }
    }

    if (formData.connection_type === 'oficial') {
      if (!formData.whatsapp_number?.trim()) {
        newErrors.whatsapp_number = 'Número do WhatsApp é obrigatório';
      }
      if (!formData.phone_id?.trim()) {
        newErrors.phone_id = 'ID Telefone é obrigatório';
      }
      if (!formData.business_account_id?.trim()) {
        newErrors.business_account_id = 'ID Conta Comercial é obrigatório';
      }
      if (!formData.permanent_token?.trim()) {
        newErrors.permanent_token = 'Token Permanente é obrigatório';
      }
      if (!formData.chatconnect_account_id?.trim()) {
        newErrors.chatconnect_account_id = 'ID da Conta ChatConnect é obrigatório';
      }
      if (!formData.chatconnect_oficial_inbox_id?.trim()) {
        newErrors.chatconnect_oficial_inbox_id = 'ID da Caixa de Entrada Oficial é obrigatório';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const url = editingConnection 
        ? `/whatsapp/${editingConnection.id}`
        : '/whatsapp/create';
      
      const method = editingConnection ? 'PUT' : 'POST';

      // Debug: verificar client.id
      console.log('🔍 [ApiTab] Client object:', client);
      console.log('🔍 [ApiTab] Client ID sendo usado:', client.id);
      console.log('🔍 [ApiTab] Form data:', formData);

      const connectionPayload: any = { 
        ...formData, 
        client_id: client.id 
      };
      
      console.log('🔍 [ApiTab] Payload final:', connectionPayload);

      if (connectionPayload.permanent_token && String(connectionPayload.permanent_token).includes('*****')) {
        delete connectionPayload.permanent_token; // não alterar token salvo
      }

      // Salvar conexão
      let connectionResponse: ApiResponse<ConnectionResponse>;
      if (method === 'POST') {
        connectionResponse = await apiService.post(url, connectionPayload) as ApiResponse<ConnectionResponse>;
      } else {
        connectionResponse = await apiService.put(url, connectionPayload) as ApiResponse<ConnectionResponse>;
      }

      await loadConnections();
      handleCancelForm();
      toast({
        title: 'Conexão salva',
        description: editingConnection ? 'Conexão atualizada com sucesso.' : 'Nova conexão criada com sucesso.',
      });
    } catch (error: any) {
      console.error('Erro ao salvar conexão:', error.message);
      const msg = error?.message ?? 'Erro ao salvar conexão';
      toast({
        title: 'Erro ao salvar',
        description: msg,
        variant: 'destructive',
      });
      alert(`Erro ao salvar: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (connection: WhatsappConnection) => {
    setEditingConnection(connection);
    setFormData(connection);
    setShowForm(true);
  };

  const handleDelete = async (connectionId: number) => {
    if (!confirm('Tem certeza que deseja deletar esta conexão?')) return;

    try {
      await apiService.delete(`/whatsapp/${connectionId}`);
        await loadConnections();
    } catch (error: any) {
      console.error('Erro ao deletar conexão:', error.message);
      alert(`Erro ao deletar: ${error.message}`);
    }
  };

  const handleTest = async (connectionId: number) => {
    setTestingConnection(connectionId);
    try {
      const data = await apiService.post(`/monitoring/check/${connectionId}`) as ApiResponse<ConnectionResponse>;
        const status = data.data?.connection.is_online ? 'ONLINE 🟢' : 'OFFLINE 🔴';
        alert(`Status da conexão: ${status}\n\nÚltima verificação: ${new Date(data.data?.connection.last_checked_at || new Date()).toLocaleString('pt-BR')}`);
        
        // Recarregar conexões para mostrar status atualizado
        await loadConnections();
    } catch (error: any) {
      console.error('Erro ao testar conexão:', error.message);
      alert(`Erro ao testar conexão: ${error.message}`);
    } finally {
      setTestingConnection(null);
    }
  };

  const handleCheckStatus = async (connectionId: number) => {
    setTestingConnection(connectionId);
    try {
      const data = await apiService.post(`/whatsapp/${connectionId}/check-status`) as ApiResponse<ConnectionResponse>;
      
      if (data.success && data.data) {
        const status = data.data.connection.is_online ? 'ONLINE 🟢' : 'OFFLINE 🔴';
        const connectionStatus = data.data.connection.connection_status;
        
        let message = `Status da conexão: ${status}\n`;
        message += `Status detalhado: ${connectionStatus}\n`;
        message += `Última verificação: ${new Date(data.data.connection.last_checked_at || new Date()).toLocaleString('pt-BR')}\n`;
        
        if ((data.data.connection as any).api_response) {
          const apiResponse = (data.data.connection as any).api_response;
          if (apiResponse.error) {
            message += `\nErro: ${apiResponse.error}`;
          } else if (apiResponse.verified !== undefined) {
            message += `\nVerificado: ${apiResponse.verified ? 'Sim' : 'Não'}`;
          }
        }
        
        alert(message);
      } else {
        alert(`Erro: ${data.error || 'Erro desconhecido'}`);
      }
      
      // Recarregar conexões para mostrar status atualizado
      await loadConnections();
    } catch (error: any) {
      console.error('Erro ao verificar status da conexão:', error);
      alert(`Erro ao verificar status: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setTestingConnection(null);
    }
  };

  const handleFetchTemplates = async (connectionId: number) => {
    setFetchingTemplatesConnection(connectionId);
    try {
      const data = await apiService.get(`/whatsapp/${connectionId}/templates`) as ApiResponse<TemplateCountResponse>;
      
      if (data.success && data.data) {
        alert(`Templates carregados: ${data.data.count} encontrados`);
      } else {
        alert(`Erro: ${data.error || 'Erro desconhecido'}`);
      }
    } catch (error: any) {
      console.error('Erro ao buscar templates:', error);
      
      // Tratar erros específicos
      if (error.response?.status === 401) {
        alert('Token inválido ou expirado. Atualize sua conexão com a API Meta.');
      } else if (error.response?.status === 403) {
        alert('Acesso negado. Verifique as permissões do token.');
      } else {
        alert(`Erro ao buscar templates: ${error.message || 'Erro desconhecido'}`);
      }
    } finally {
      setFetchingTemplatesConnection(null);
    }
  };

  const handleSyncTemplates = async (connectionId: number) => {
    setFetchingTemplatesConnection(connectionId);
    try {
      const data = await apiService.get(`/whatsapp/${connectionId}/templates/sync`) as ApiResponse<TemplateCountResponse>;
      
      if (data.success && data.data) {
        alert(`Templates sincronizados: ${data.data.count} encontrados`);
      } else {
        alert(`Erro: ${data.error || 'Erro desconhecido'}`);
      }
    } catch (error: any) {
      console.error('Erro ao sincronizar templates:', error);
      
      // Tratar erros específicos
      if (error.response?.status === 401) {
        alert('Token inválido ou expirado. Atualize sua conexão com a API Meta.');
      } else if (error.response?.status === 403) {
        alert('Acesso negado. Verifique as permissões do token.');
      } else {
        alert(`Erro ao sincronizar templates: ${error.message || 'Erro desconhecido'}`);
      }
    } finally {
      setFetchingTemplatesConnection(null);
    }
  };




  const handleCancelForm = () => {
    setShowForm(false);
    setEditingConnection(null);
    setFormData({
      connection_type: 'extra_oficial',
      name: '',
      is_active: true
    });
    setErrors({});
  };

  const handleConfigureStrategies = (connection: WhatsappConnection) => {
    console.log('🎯 [ApiTab] Configurando estratégias para conexão:', connection);
    console.log('🎯 [ApiTab] Client ID:', client?.id);
    
    // Validar se o cliente é válido
    if (!client?.id || client.id <= 0) {
      alert('Erro: Cliente não identificado. Verifique se você está logado corretamente.');
      return;
    }
    
    console.log('🎯 [ApiTab] Abrindo modal de estratégias...');
    setSelectedConnectionForStrategy(connection);
    setShowStrategyModal(true);
    console.log('🎯 [ApiTab] Modal deve estar aberto agora');
  };

  const handleCloseStrategyModal = () => {
    setShowStrategyModal(false);
    setSelectedConnectionForStrategy(null);
  };

  // Função para carregar templates da Meta
  const loadMetaTemplates = async (connectionId: number) => {
    try {
      setLoadingTemplates(true);
      console.log('🔍 Carregando templates da Meta para connectionId:', connectionId);
      const data = await apiService.get(`/whatsapp/${connectionId}/templates`) as any;
      console.log('🔍 Templates da Meta carregados:', data);
      setMetaTemplates(data.templates || []);
    } catch (error) {
      console.error('❌ Erro ao carregar templates da Meta:', error);
      setMetaTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Função para exportar templates em PDF
  const exportTemplatesToPDF = () => {
    if (!selectedConnection) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 25; // Margem aumentada
    const bottomMargin = 30; // Margem inferior para evitar corte
    let yPosition = margin;

    // Função para verificar se precisa de nova página
    const checkNewPage = (requiredSpace: number) => {
      if (yPosition + requiredSpace > pageHeight - bottomMargin) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Função para adicionar cabeçalho em cada página
    const addHeader = () => {
      // Linha superior decorativa verde
      doc.setDrawColor(34, 197, 94); // Verde do sistema
      doc.setLineWidth(4);
      doc.line(0, 15, pageWidth, 15);
      
      // Logo/Ícone (círculo verde)
      doc.setFillColor(34, 197, 94);
      doc.circle(25, 25, 8, 'F');
      
      // Texto do logo
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(34, 197, 94);
      doc.text('Portal Átomos', 40, 30);
      
      // Subtítulo
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Sistema de Gestão WhatsApp', 40, 35);
      
      // Linha separadora
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(25, 40, pageWidth - 25, 40);
      
      yPosition = 50; // Posição inicial do conteúdo
    };

    // Adicionar cabeçalho na primeira página
    addHeader();

    // Título principal
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Templates da Meta API', margin, yPosition);
    yPosition += 15;

    // Informações da conexão em caixa estilizada
    doc.setFillColor(240, 253, 244); // Verde claro
    doc.rect(margin, yPosition - 5, pageWidth - 2 * margin, 25, 'F');
    doc.setDrawColor(34, 197, 94); // Verde do sistema
    doc.setLineWidth(1);
    doc.rect(margin, yPosition - 5, pageWidth - 2 * margin, 25, 'S');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Conexão: ${selectedConnection.name}`, margin + 5, yPosition + 2);
    doc.text(`Tipo: API Oficial`, margin + 5, yPosition + 8);
    doc.text(`Data de exportação: ${new Date().toLocaleDateString('pt-BR')}`, margin + 5, yPosition + 14);
    yPosition += 30;

    // Tabela de preços por categoria - Layout elegante
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(34, 197, 94); // Verde do sistema
    doc.text('Preços por Categoria', margin, yPosition);
    yPosition += 12;

    // Container da tabela com sombra sutil
    const tableStartX = margin;
    const tableWidth = pageWidth - 2 * margin;
    const rowHeight = 18;

    // Cabeçalho da tabela com gradiente verde
    doc.setFillColor(34, 197, 94);
    doc.roundedRect(tableStartX, yPosition - 5, tableWidth, rowHeight, 3, 3, 'F');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Categoria', tableStartX + 15, yPosition + 5);
    doc.text('Valor por Mensagem', tableStartX + 120, yPosition + 5);
    doc.text('Descrição', tableStartX + 200, yPosition + 5);
    yPosition += rowHeight;

    // Dados da tabela com valores diretos em reais
    const pricingData = [
      { 
        category: 'Marketing', 
        price: 'R$ 0,364', 
        description: 'Promoções e ofertas',
        color: [34, 197, 94] // Verde
      },
      { 
        category: 'Utility', 
        price: 'R$ 0,047', 
        description: 'Notificações e lembretes',
        color: [59, 130, 246] // Azul
      },
      { 
        category: 'Authentication', 
        price: 'R$ 0,184', 
        description: 'Códigos de verificação',
        color: [239, 68, 68] // Vermelho
      }
    ];

    pricingData.forEach((row, index) => {
      // Cor de fundo alternada
      if (index % 2 === 0) {
        doc.setFillColor(248, 250, 252);
      } else {
        doc.setFillColor(255, 255, 255);
      }
      doc.roundedRect(tableStartX, yPosition - 5, tableWidth, rowHeight, 2, 2, 'F');
      
      // Borda sutil
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.roundedRect(tableStartX, yPosition - 5, tableWidth, rowHeight, 2, 2, 'S');
      
      // Categoria com cor específica
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(row.color[0], row.color[1], row.color[2]);
      doc.text(row.category, tableStartX + 15, yPosition + 5);
      
      // Preço em destaque
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(row.price, tableStartX + 120, yPosition + 5);
      
      // Descrição
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(row.description, tableStartX + 200, yPosition + 5);
      
      yPosition += rowHeight;
    });

    // Nota informativa elegante
    yPosition += 10;
    doc.setFillColor(240, 253, 244);
    doc.roundedRect(margin, yPosition - 3, pageWidth - 2 * margin, 12, 2, 2, 'F');
    doc.setDrawColor(34, 197, 94);
    doc.setLineWidth(1);
    doc.roundedRect(margin, yPosition - 3, pageWidth - 2 * margin, 12, 2, 2, 'S');
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(34, 197, 94);
    doc.text('Valores já incluem IOF de 10% conforme regulamentação brasileira', margin + 8, yPosition + 5);
    yPosition += 20;

    // Templates da Meta
    if (metaTemplates.length > 0) {
      metaTemplates.forEach((template, index) => {
        // Verificar se precisa de nova página
        const templateHeight = 50; // Altura estimada por template
        if (checkNewPage(templateHeight)) {
          addHeader();
        }

        // Cabeçalho do template sem borda
        doc.setFillColor(248, 250, 252); // Cinza claro
        doc.roundedRect(margin, yPosition - 3, pageWidth - 2 * margin, 18, 3, 3, 'F');

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`${index + 1}. ${template.name}`, margin + 5, yPosition + 5);
        yPosition += 12;

        // Status e categoria
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Status: ${template.status} | Categoria: ${template.category}`, margin + 5, yPosition);
        yPosition += 5;
        doc.text(`ID: ${template.id} | Idioma: ${template.language}`, margin + 5, yPosition);
        yPosition += 8;

        // Conteúdo do template
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        const content = template.components?.find((c: any) => c.type === 'BODY')?.text || 'Sem conteúdo';
        const splitContent = doc.splitTextToSize(content, pageWidth - 2 * margin - 10);
        
        // Verificar se o conteúdo cabe na página
        const contentHeight = splitContent.length * 4 + 10;
        if (checkNewPage(contentHeight)) {
          addHeader();
        }
        
        doc.text(splitContent, margin + 5, yPosition);
        yPosition += splitContent.length * 4 + 15;
      });
    } else {
      // Mensagem quando não há templates
      if (checkNewPage(20)) {
        addHeader();
      }
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text('Nenhum template encontrado para esta conexão.', margin, yPosition);
    }

    // Rodapé em todas as páginas
    const addFooter = (pageNum: number) => {
      const originalY = yPosition;
      yPosition = pageHeight - 20;
      
      // Linha separadora do rodapé em verde
      doc.setDrawColor(34, 197, 94);
      doc.setLineWidth(1);
      doc.line(25, yPosition - 5, pageWidth - 25, yPosition - 5);
      
      // Texto do rodapé
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(34, 197, 94);
      doc.text(`Portal Átomos - Página ${pageNum}`, 25, yPosition);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, pageWidth - 25 - doc.getTextWidth(`Gerado em: ${new Date().toLocaleString('pt-BR')}`), yPosition);
      
      yPosition = originalY;
    };

    // Adicionar rodapé em todas as páginas
    const totalPages = doc.internal.pages.length - 1; // -1 porque pages inclui uma página vazia
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(i);
    }

    // Salvar o PDF
    const fileName = `templates_meta_${selectedConnection.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    // Mostrar toast de sucesso (se disponível)
    console.log(`Templates exportados para ${fileName}`);
  };

  const getConnectionIcon = (type: string) => {
    return type === 'oficial' ? Shield : Zap;
  };

  const getConnectionColor = (type: string) => {
    return type === 'oficial' ? 'bg-blue-500' : 'bg-green-500';
  };

  const getStatusBadge = (connection: WhatsappConnection) => {
    if (!connection.chatconnect_account_id) {
      return <Badge variant="secondary">Sem Monitoramento</Badge>;
    }

    if (connection.is_online === true) {
      return <Badge className="bg-green-500 hover:bg-green-600">Online</Badge>;
    } else if (connection.is_online === false) {
      return <Badge variant="destructive">Offline</Badge>;
    } else {
      return <Badge variant="outline">Status Desconhecido</Badge>;
    }
  };

  const formatLastCheck = (lastCheck?: string) => {
    if (!lastCheck) return 'Nunca verificado';
    return new Date(lastCheck).toLocaleString('pt-BR');
  };

  return (
    <TabsContent value="api" className="space-y-6 mt-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-green-600" />
            API de Envio WhatsApp
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Configure conexões para envio de mensagens via WhatsApp
          </p>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-gray-600 whitespace-nowrap">Modo de envio:</Label>
            <Select value={envioVersion} onValueChange={(v) => handleEnvioVersionChange(v as 'v1' | 'v2' | 'email')} disabled={loadingAtomosChatConfig}>
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="v1">Envios - V1</SelectItem>
                <SelectItem value="v2">Envios - V2 (ÁtomosChat)</SelectItem>
                <SelectItem value="email">Envios - Email</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {envioVersion === 'v1' && (
            <>
              <ToggleGroup type="single" value={apiType} onValueChange={(v)=> v && setApiType(v as any)}>
                <ToggleGroupItem value="oficial">API Oficial</ToggleGroupItem>
                <ToggleGroupItem value="extra_oficial">API Extra</ToggleGroupItem>
              </ToggleGroup>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowForm(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Conexão
                </Button>
                <Button
                  onClick={loadConnectChatConnections}
                  variant="outline"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Carregar do ConnectChat
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modo Email: configuração de remetente do cliente */}
      {envioVersion === 'email' && (
        <EmailRemetenteSection client={client} />
      )}

      {/* Ativar API Meta — sempre visível quando V1 está selecionado */}
      {envioVersion === 'v1' && (
        <Card className="border-2 border-purple-200 bg-purple-50/50">
          <CardContent className="py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Key className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="font-semibold text-purple-900">Ativar API Meta</div>
                <div className="text-sm text-purple-700">
                  Configure 2FA da conta Meta e capture o phone_id automaticamente
                </div>
              </div>
            </div>
            <Button
              onClick={() => setShowMetaActivationWizard(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Key className="w-4 h-4 mr-2" />
              Iniciar wizard
            </Button>
          </CardContent>
        </Card>
      )}


      {/* Envios V2 (ÁtomosChat): Form de cadastro/edição visível somente em V2 */}
      {envioVersion === 'v2' && showConfigForm && (
        <AtomosChatApiGuide
          form={atomosChatConfigForm}
          onFormChange={(field, value) => setAtomosChatConfigForm(prev => ({ ...prev, [field]: value }))}
          onSave={saveAtomosChatConfig}
          saving={savingAtomosChatConfig}
          error={errors.atomosChat}
          mode={editingConfigId == null ? 'create' : 'edit'}
          onCancel={atomosChatConfigs.length > 0 ? cancelConfigEdit : undefined}
          clientId={client.id}
          editingConfigId={editingConfigId}
        />
      )}

      {/* Lista de conexões V2 cadastradas — sempre que houver pelo menos uma OU quando o cliente está em V2 */}
      {envioVersion !== 'email' && (envioVersion === 'v2' || atomosChatConfigs.length > 0) && (
        <Card className="border-2 border-teal-200 bg-teal-50/50">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-teal-800">
                  <Smartphone className="w-5 h-5" />
                  Conexões V2 cadastradas (ÁtomosChat)
                </CardTitle>
                <CardDescription className="text-teal-600">
                  Contas/canais ÁtomosChat deste cliente. Cada estratégia V2 escolhe qual conexão envia.
                </CardDescription>
              </div>
              {envioVersion === 'v2' && !showConfigForm && (
                <Button onClick={startNewConfig} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar conexão
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {envioVersion === 'v1' && atomosChatConfigs.length > 0 && (
              <div className="mb-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Estas conexões só são usadas quando o modo de envio do cliente é <strong>V2</strong>. Mude o seletor acima para "Envios - V2" para ativá-las.
              </div>
            )}
            {loadingAtomosChatConfig ? (
              <div className="text-sm text-gray-600">Carregando conexões...</div>
            ) : atomosChatConfigs.length === 0 ? (
              <div className="text-sm text-gray-600">
                Nenhuma conexão V2 cadastrada. Use o formulário acima para criar a primeira.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-teal-200 text-left text-teal-800">
                      <th className="py-2 pr-3 font-semibold">Status</th>
                      <th className="py-2 pr-3 font-semibold">Conta</th>
                      <th className="py-2 pr-3 font-semibold">Canal</th>
                      <th className="py-2 pr-3 font-semibold">Telefone</th>
                      <th className="py-2 pr-3 font-semibold">Channel ID</th>
                      <th className="py-2 pr-3 font-semibold">Token</th>
                      <th className="py-2 pr-3 font-semibold">Criada em</th>
                      <th className="py-2 pr-3 font-semibold text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {atomosChatConfigs.map((cfg) => (
                      <tr key={cfg.id} className={`border-b border-teal-100 ${editingConfigId === cfg.id ? 'bg-teal-100/40' : ''} ${cfg.is_active === false ? 'opacity-60' : ''}`}>
                        <td className="py-2 pr-3">
                          {cfg.is_active === false ? (
                            <Badge variant="secondary" className="text-xs">Inativa</Badge>
                          ) : (
                            <Badge variant="default" className="text-xs bg-teal-600">Ativa</Badge>
                          )}
                        </td>
                        <td className="py-2 pr-3 font-medium text-gray-900">{cfg.account_name || '-'}</td>
                        <td className="py-2 pr-3 text-gray-700">{cfg.channel_name || '-'}</td>
                        <td className="py-2 pr-3 text-gray-700">{cfg.channel_phone || '-'}</td>
                        <td className="py-2 pr-3 text-gray-700 font-mono text-xs">{cfg.channel_id || '-'}</td>
                        <td className="py-2 pr-3 text-gray-500 font-mono text-xs">••••••••</td>
                        <td className="py-2 pr-3 text-gray-600">
                          {cfg.created_at ? new Date(cfg.created_at).toLocaleDateString('pt-BR') : '-'}
                        </td>
                        <td className="py-2 pr-0 text-right">
                          <div className="flex justify-end gap-1">
                            {cfg.is_active === false ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-teal-700 hover:text-teal-800"
                                onClick={() => cfg.id && reactivateAtomosChatConfig(cfg.id)}
                                disabled={savingAtomosChatConfig || envioVersion !== 'v2'}
                                title={envioVersion === 'v2' ? 'Reativar conexão' : 'Mude para V2 para reativar'}
                              >
                                Reativar
                              </Button>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => editAtomosChatConfig(cfg)}
                                  disabled={savingAtomosChatConfig || deletingConfigId === cfg.id || envioVersion !== 'v2'}
                                  title={envioVersion === 'v2' ? 'Editar conexão' : 'Mude para V2 para editar'}
                                >
                                  <Edit className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => cfg.id && deleteAtomosChatConfig(cfg.id)}
                                  disabled={deletingConfigId === cfg.id || envioVersion !== 'v2'}
                                  title={envioVersion === 'v2' ? 'Remover conexão' : 'Mude para V2 para remover'}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {envioVersion === 'v2' && (
        <>
          <Card className="border-2 border-teal-200 bg-teal-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-teal-800">
                <MessageSquare className="w-5 h-5" />
                Templates ÁtomosChat
              </CardTitle>
              <CardDescription className="text-teal-600">
                Carregue os templates da API ÁtomosChat (Type=ATTENDANCE) para uma conexão específica
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                <div className="flex-1 min-w-[240px]">
                  <Label className="text-sm">Conexão</Label>
                  <Select
                    value={templatesConfigId ? String(templatesConfigId) : ''}
                    onValueChange={(v) => setTemplatesConfigId(v ? Number(v) : null)}
                    disabled={atomosChatConfigs.filter(c => c.is_active !== false).length === 0}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Selecione uma conexão" />
                    </SelectTrigger>
                    <SelectContent>
                      {atomosChatConfigs.filter(c => c.is_active !== false).map((cfg) => (
                        <SelectItem key={cfg.id} value={String(cfg.id)}>
                          {cfg.account_name}{cfg.channel_phone ? ` — ${cfg.channel_phone}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={loadAtomosChatTemplates}
                  disabled={loadingAtomosChatTemplates || atomosChatConfigs.length === 0 || !templatesConfigId}
                >
                  {loadingAtomosChatTemplates ? 'Carregando...' : 'Carregar templates'}
                </Button>
              </div>
              {atomosChatConfigs.length === 0 && (
                <span className="text-sm text-amber-600 mt-2 block">
                  Cadastre uma conexão V2 antes de carregar os templates.
                </span>
              )}
              {atomosChatTemplates.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Templates encontrados ({atomosChatTemplates.length})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {atomosChatTemplates.slice(0, 6).map((template) => (
                      <div key={template.id} className="border rounded-lg p-3 bg-gray-50">
                        <div className="font-medium text-sm">{template.name}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {template.status} • {template.categoryName ?? '-'}
                        </div>
                      </div>
                    ))}
                    {atomosChatTemplates.length > 6 && (
                      <div className="border rounded-lg p-3 bg-gray-100 flex items-center justify-center">
                        <span className="text-sm text-gray-600">
                          +{atomosChatTemplates.length - 6} mais...
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Webhook de Boleto (Automação V2) */}
          <WebhooksTab client={client} />
        </>
      )}

      {/* Seletor de Conexões e Exportação PDF (Envios V1) */}
      {envioVersion === 'v1' && connections.length > 0 && (
        <Card className="border-2 border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <MessageSquare className="w-5 h-5" />
              Templates da Meta API
            </CardTitle>
            <CardDescription className="text-blue-600">
              Selecione uma conexão oficial para visualizar e exportar templates da Meta
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="connection-select">Conexão WhatsApp Oficial</Label>
                <Select 
                  value={selectedConnection?.id?.toString() || ''} 
                  onValueChange={(value) => {
                    const connection = connections.find(c => c.id === parseInt(value));
                    setSelectedConnection(connection || null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma conexão oficial" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections
                      .filter(conn => conn.connection_type === 'oficial')
                      .map((connection) => (
                        <SelectItem key={connection.id} value={connection.id?.toString() || ''}>
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-blue-500" />
                            <span>{connection.name}</span>
                            <Badge variant="default">API Oficial</Badge>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedConnection && (
                <div className="ml-4">
                  <Button 
                    onClick={exportTemplatesToPDF}
                    variant="outline"
                    className="flex items-center gap-2"
                    disabled={loadingTemplates}
                  >
                    <Download className="h-4 w-4" />
                    {loadingTemplates ? 'Carregando...' : 'Exportar PDF'}
                  </Button>
                </div>
              )}
            </div>

            {/* Mostrar templates carregados */}
            {selectedConnection && metaTemplates.length > 0 && (
              <div className="mt-4">
                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Templates encontrados ({metaTemplates.length})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {metaTemplates.slice(0, 6).map((template, index) => (
                      <div key={template.id} className="border rounded-lg p-3 bg-gray-50">
                        <div className="font-medium text-sm">{template.name}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {template.status} • {template.category}
                        </div>
                      </div>
                    ))}
                    {metaTemplates.length > 6 && (
                      <div className="border rounded-lg p-3 bg-gray-100 flex items-center justify-center">
                        <span className="text-sm text-gray-600">
                          +{metaTemplates.length - 6} mais...
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Seção Configurações por Conexão */}
      <Card className="border-2 border-purple-200 bg-purple-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <Zap className="w-5 h-5" />
            Configurações por Conexão
          </CardTitle>
          <CardDescription className="text-purple-600">
            Configure limites diários e delay por conexão
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 mb-4">
          </div>

          {/* Seleção de Conexão e Configuração Única */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Configuração de Rate Limits
              </h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  console.log('🔄 [ApiTab] Botão Atualizar clicado - forçando reload de todos os dados');
                  loadRedisLimits();
                  loadPersistedConfig();
                  
                  // Mostrar feedback visual
                  const button = document.querySelector('button[onClick*="Atualizar"]');
                  if (button) {
                    const originalText = button.textContent;
                    button.textContent = 'Atualizando...';
                    setTimeout(() => {
                      button.textContent = originalText;
                    }, 2000);
                  }
                }}
                className="text-xs bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                disabled={loadingRedisLimits}
              >
                {loadingRedisLimits ? (
                  <>
                    <div className="w-4 h-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent mr-1" />
                    Carregando...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Atualizar Progresso
                  </>
                )}
              </Button>
            </div>
            
            {/* Dropdown para Seleção de Conexão */}
            <div className="mb-6">
              <Label htmlFor="connection-select" className="text-sm font-medium text-gray-700 mb-2 block">
                Selecione a conexão para configurar:
              </Label>
              {loading && envioVersion === 'v1' && (
                <p className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                  <span className="inline-block w-4 h-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                  Carregando conexões...
                </p>
              )}
              <Select
                value={selectedConnectionId ? selectedConnectionId.toString() : ''}
                onValueChange={(value) => {
                  const connectionId = parseInt(value);
                  setSelectedConnectionId(connectionId);
                  console.log('🔄 [ApiTab] Conexão selecionada:', connectionId);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione uma conexão..." />
                </SelectTrigger>
                <SelectContent>
                  {connections
                    .filter(c => c.connection_type === apiType)
                    .map((connection) => (
                      <SelectItem key={connection.id} value={connection.id!.toString()}>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-xs">{connection.name.charAt(0)}</span>
                          </div>
                          <div>
                            <div className="font-medium">{connection.name}</div>
                            <div className="text-xs text-gray-500">ID: {connection.id}</div>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Configuração da Conexão Selecionada */}
            {selectedConnectionId && (() => {
              const connection = connections.find(c => c.id === selectedConnectionId && c.connection_type === apiType);
              if (!connection) return null;
              
              const limitData = redisLimits[connection.id!];
              const metaLimitData = metaLimits[connection.id!];
              const rateLimitData = rateLimitStats[connection.id!];
              
              // Usar dados configurados localmente com fallback para Redis
              const currentCount = limitData?.currentCount || 0;
              const dailyLimit = connectionLimits[connection.id!] || limitData?.dailyLimit || 0;
              const remaining = dailyLimit > 0 ? Math.max(0, dailyLimit - currentCount) : 0;
              const delaySeconds = connectionDelays[connection.id!] || connection.min_interval_seconds || connection.delay_seg || 1;
              
              return (
                <div key={connection.id} className="bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                  {/* Header da Conexão */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">{connection.name.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-gray-900">{connection.name}</div>
                          {(connectionLimits[connection.id!] || connectionDelays[connection.id!]) && (
                            <div className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                              ✓ Configurado
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">ID: {connection.id}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-2 h-2 rounded-full ${
                          dailyLimit === 0 ? 'bg-gray-400' : 
                          remaining > 0 ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <span className="text-sm font-medium text-gray-700">
                          {currentCount} / {dailyLimit > 0 ? dailyLimit : 'Sem limite'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {dailyLimit > 0 ? (remaining > 0 ? `${remaining} restantes` : 'Limite atingido') : 'Ilimitado'}
                      </div>
                      {/* Debug info */}
                      <div className="text-xs text-blue-600 mt-1">
                        Redis: {limitData?.currentCount || 0} | Local: {connectionLimits[connection.id!] || 'N/A'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Barra de progresso */}
                  {dailyLimit > 0 && (
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Progresso diário</span>
                        <span>{Math.round((currentCount / dailyLimit) * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            (currentCount / dailyLimit) >= 1 ? 'bg-red-500' : 
                            (currentCount / dailyLimit) >= 0.8 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(100, (currentCount / dailyLimit) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Configurações de Rate Limit */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Limite Diário */}
                    <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <span className="text-sm font-medium text-green-800">Limite Diário</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="50000"
                          placeholder="Ex: 1000"
                          value={connectionLimits[connection.id!] || dailyLimit || ''}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 0;
                            setConnectionLimits(prev => ({ ...prev, [connection.id!]: value }));
                          }}
                          className="w-24 text-sm"
                        />
                        <span className="text-xs text-green-600">msgs/dia</span>
                      </div>
                      {(connection.window_start_formatted || connection.window_end_formatted) && (
                        <div className="mt-2 text-xs text-green-700">
                          Janela ativa: {(connection.window_start_formatted || connection.window_start || '00:00')} - {(connection.window_end_formatted || connection.window_end || '23:59')}
                        </div>
                      )}
                    </div>

                    {/* Delay entre Mensagens */}
                    <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-blue-800">Delay entre Mensagens</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          max="60"
                          placeholder="1"
                          value={connectionDelays[connection.id!] || delaySeconds}
                          onChange={(e) => {
                            const value = Math.max(1, parseInt(e.target.value) || 1);
                            setConnectionDelays(prev => ({ ...prev, [connection.id!]: value }));
                          }}
                          className="w-20 text-sm"
                        />
                        <span className="text-xs text-blue-600">seg (mín. 1s)</span>
                      </div>
                    </div>
                  </div>

                  {/* Janela de Tempo para Envio */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Horário de Início */}
                    <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-purple-800">Horário de Início</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={
                            connectionWindows[connection.id!]?.start ||
                            connection.window_start_formatted ||
                            connection.window_start ||
                            '00:00'
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            setConnectionWindows(prev => ({
                              ...prev,
                              [connection.id!]: {
                                start: value,
                                end:
                                  prev[connection.id!]?.end ||
                                  connection.window_end_formatted ||
                                  connection.window_end ||
                                  '23:59'
                              }
                            }));
                          }}
                          className="w-24 text-sm"
                        />
                        <span className="text-xs text-purple-600">(00:00-23:59)</span>
                      </div>
                    </div>

                    {/* Horário de Fim */}
                    <div className="p-3 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-orange-800">Horário de Fim</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={
                            connectionWindows[connection.id!]?.end ||
                            connection.window_end_formatted ||
                            connection.window_end ||
                            '23:59'
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            setConnectionWindows(prev => ({
                              ...prev,
                              [connection.id!]: {
                                start:
                                  prev[connection.id!]?.start ||
                                  connection.window_start_formatted ||
                                  connection.window_start ||
                                  '00:00',
                                end: value
                              }
                            }));
                          }}
                          className="w-24 text-sm"
                        />
                        <span className="text-xs text-orange-600">(00:00-23:59)</span>
                      </div>
                    </div>
                  </div>

                  {/* Botões de Ação */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-3">
                      <Button
                        size="sm"
                        className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                        onClick={async () => {
                          console.log('🔘 [ApiTab] BOTÃO SALVAR CONFIGURAÇÃO para conexão:', connection.id);
                          const delayValue = connectionDelays[connection.id!] || delaySeconds;
                          const limitValue = connectionLimits[connection.id!] || dailyLimit || 0;
                          const windowData = connectionWindows[connection.id!];
                          
                          console.log('🔘 [ApiTab] Estado completo:', {
                            connectionId: connection.id,
                            connectionWindows: connectionWindows,
                            currentWindowData: windowData,
                            connectionWindowStart: connection.window_start_formatted || connection.window_start,
                            connectionWindowEnd: connection.window_end_formatted || connection.window_end
                          });
                          
                          console.log('🔘 [ApiTab] Valores a serem salvos:', { 
                            delay: delayValue, 
                            limit: limitValue,
                            window: windowData 
                          });
                          
                          // Salvar configuração básica
                          const configSaved = await saveConnectionConfig(connection.id!, limitValue, delayValue);
                          console.log('🔘 [ApiTab] Configuração básica salva:', configSaved);
                          
                          // Sempre tentar salvar janela de tempo se a configuração básica foi salva
                          if (configSaved) {
                            // Usar dados do estado ou valores padrão da conexão
                            const startTime = windowData?.start || connection.window_start_formatted || connection.window_start || '09:00';
                            const endTime = windowData?.end || connection.window_end_formatted || connection.window_end || '18:00';
                            
                            console.log('🕐 [ApiTab] Salvando janela de tempo:', { startTime, endTime });
                            await saveConnectionWindow(connection.id!, startTime, endTime);
                          } else {
                            console.log('⚠️ [ApiTab] Configuração básica falhou, não salvando janela de tempo');
                          }
                        }}
                        disabled={savingConnectionConfig[connection.id!] || savingWindow[connection.id!]}
                      >
                        {savingConnectionConfig[connection.id!] || savingWindow[connection.id!] ? (
                          <>
                            <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-1" />
                            Salvando...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            Salvar Configuração
                          </>
                        )}
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resetConnectionCounter(connection.id!)}
                        disabled={resettingCounter === connection.id}
                        className="border-orange-300 text-orange-600 hover:bg-orange-50"
                      >
                        {resettingCounter === connection.id ? (
                          <>
                            <div className="w-4 h-4 animate-spin rounded-full border-2 border-orange-500 border-t-transparent mr-1" />
                            Resetando...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Reset Manual
                          </>
                        )}
                      </Button>
                    </div>
                    
                    {/* Status de Performance e Debug */}
                    <div className="text-right space-y-1">
                      <div className="text-xs text-gray-500">
                        Performance: {currentCount > 0 && dailyLimit > 0 ? Math.round((currentCount / dailyLimit) * 100) : 0}%
                      </div>
                      <div className="text-xs text-gray-400">
                        Delay atual: {connectionDelays[connection.id!] || delaySeconds}s
                      </div>
                      <div className="text-xs text-gray-400">
                        Limite: {connectionLimits[connection.id!] || dailyLimit || 'Não configurado'}
                      </div>
                      {(() => {
                        try {
                          const configKey = `rate_limit_config_${client?.id}`;
                          const savedConfig = localStorage.getItem(configKey);
                          if (savedConfig) {
                            const parsed = JSON.parse(savedConfig);
                            const connectionConfig = parsed[connection.id!];
                            if (connectionConfig?.savedAt) {
                              const savedDate = new Date(connectionConfig.savedAt);
                              return (
                                <div className="text-xs text-green-600">
                                  Salvo: {savedDate.toLocaleTimeString()}
                                </div>
                              );
                            }
                          }
                        } catch (error) {
                          console.error('Erro ao verificar data de salvamento:', error);
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  {/* Limites da Meta */}
                  {metaLimitData && (
                    <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="text-sm font-medium text-green-800 mb-2">📊 Limites da Meta:</div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-green-700">Meta diário:</span>
                          <span className="ml-1 font-medium">{metaLimitData.daily_limit || 0}</span>
                        </div>
                        <div>
                          <span className="text-green-700">Usado hoje:</span>
                          <span className="ml-1 font-medium">{metaLimitData.current_usage || 0}</span>
                        </div>
                        <div>
                          <span className="text-green-700">Restantes:</span>
                          <span className="ml-1 font-medium">{metaLimitData.remaining || 0}</span>
                        </div>
                        <div>
                          <span className="text-green-700">Reset:</span>
                          <span className="ml-1 font-medium">{metaLimitData.reset_time ? new Date(metaLimitData.reset_time).toLocaleTimeString() : 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Mensagem quando nenhuma conexão está selecionada */}
            {!selectedConnectionId && (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm font-medium">Selecione uma conexão para configurar os rate limits</p>
                  <p className="text-xs text-gray-400">Use o dropdown acima para escolher a conexão desejada</p>
                </div>
              </div>
            )}
          </div>

          {connections.filter(c => c.connection_type === apiType).length === 0 && (
            <div className="text-center py-4 text-gray-500">
              Nenhuma conexão encontrada para configurar limites
            </div>
          )}
        </CardContent>
      </Card>


      {/* Lista de Conexões */}
      {(() => {
        const filteredConnections = connections.filter(c => c.connection_type === apiType);
        console.log('FILTRO - apiType:', apiType);
        console.log('FILTRO - Total de conexões:', connections.length);
        console.log('FILTRO - Conexões filtradas:', filteredConnections.length);
        console.log('FILTRO - Conexões filtradas:', filteredConnections.map(c => ({ id: c.id, name: c.name, type: c.connection_type })));
        
        // Se não houver conexões do tipo selecionado para este cliente, mostrar mensagem informativa
        if (filteredConnections.length === 0) {
          return (
            <Card className="border-2 border-yellow-200 bg-yellow-50/50">
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <div className="flex justify-center mb-4">
                    <Smartphone className="w-12 h-12 text-yellow-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {apiType === 'oficial' ? 'Nenhuma API Oficial Configurada' : 'Nenhuma API Extra Oficial Configurada'}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Ainda não há {apiType === 'oficial' ? 'API Oficial' : 'API Extra Oficial'} cadastrada para este cliente.
                  </p>
                  <Button 
                    onClick={() => setShowForm(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Cadastrar {apiType === 'oficial' ? 'API Oficial' : 'API Extra Oficial'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        }
        
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredConnections.map((connection) => {
          const Icon = getConnectionIcon(connection.connection_type);
          
          return (
            <Card key={connection.id} className="relative">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full ${getConnectionColor(connection.connection_type)} flex items-center justify-center`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{connection.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={connection.connection_type === 'oficial' ? 'default' : 'secondary'}>
                          {connection.connection_type === 'oficial' ? 'API Oficial' : 'API Extra Oficial'}
                        </Badge>
                        <Badge variant={connection.is_active ? 'outline' : 'destructive'}>
                          {connection.is_active ? 'Ativa' : 'Inativa'}
                        </Badge>
                        {getStatusBadge(connection)}
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="space-y-2 text-sm text-gray-600">
                  {connection.connection_type === 'extra_oficial' ? (
                    <div>
                      <strong>ID da Conexão:</strong> {connection.chatconnect_account_id}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div><strong>Número:</strong> {connection.whatsapp_number}</div>
                      <div><strong>Phone ID:</strong> {connection.phone_id}</div>
                      <div><strong>ChatConnect ID:</strong> {connection.chatconnect_account_id}</div>
                    </div>
                  )}
                  
                  {connection.chatconnect_account_id && (
                    <div className="pt-2 border-t">
                      <div className="text-xs text-gray-500">
                        <strong>Status:</strong> {connection.connection_status || 'unknown'}
                      </div>
                      <div className="text-xs text-gray-500">
                        <strong>Última verificação:</strong> {formatLastCheck(connection.last_checked_at)}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 mt-4">

                  <Button
                    size="default"
                    variant="outline"
                    onClick={() => handleEdit(connection)}
                    className="bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700 hover:text-blue-800"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                  
                  <Button
                    size="default"
                    variant="outline"
                    onClick={() => handleDelete(connection.id!)}
                    className="bg-red-50 hover:bg-red-100 border-red-200 text-red-700 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Excluir
                  </Button>
                  
                  <Button
                    size="default"
                    variant="outline"
                    onClick={() => handleConfigureStrategies(connection)}
                    className="bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700 hover:text-purple-800"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Estratégias
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
          </div>
        );
      })()}

      {/* Formulário de Nova/Editar Conexão */}
      {showForm && (
        <Card className="border-2 border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {editingConnection ? (
                <>
                  <Edit className="w-5 h-5 text-blue-600" />
                  Editar Conexão
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 text-blue-600" />
                  Nova Conexão WhatsApp
                </>
              )}
            </CardTitle>
            <CardDescription>
              Configure os dados de conexão com a API do WhatsApp
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Tipo de Conexão */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="connection_type">Tipo de API *</Label>
                <Select
                  value={formData.connection_type}
                  onValueChange={(value) => handleInputChange('connection_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="extra_oficial">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-green-600" />
                        API Extra Oficial
                      </div>
                    </SelectItem>
                    <SelectItem value="oficial">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-600" />
                        API Oficial
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="name">Nome da Conexão *</Label>
                <Input
                  id="name"
                  placeholder="Ex: [1] API Financeiro 8409"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={errors.name ? 'border-red-500' : ''}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use o nome da caixa de entrada do ConnectChat
                </p>
                {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
              </div>
            </div>

            {/* Campos específicos para cada tipo */}
            {formData.connection_type === 'extra_oficial' && (
              <div>
                <Label htmlFor="chatconnect_account_id">ID da Conexão (Identifier ConnectChat) *</Label>
                <Input
                  id="chatconnect_account_id"
                  placeholder="Ex: GEsBpLNdYXAPUPQ3JMvpgs8X"
                  value={formData.chatconnect_account_id || ''}
                  onChange={(e) => handleInputChange('chatconnect_account_id', e.target.value)}
                  className={errors.chatconnect_account_id ? 'border-red-500' : ''}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Este é o identifier extraído do webhook do ConnectChat
                </p>
                {errors.chatconnect_account_id && (
                  <p className="text-sm text-red-500 mt-1">{errors.chatconnect_account_id}</p>
                )}
              </div>
            )}

            {formData.connection_type === 'oficial' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="whatsapp_number">Número do WhatsApp *</Label>
                    <Input
                      id="whatsapp_number"
                      placeholder="Ex: +5511999999999"
                      value={formData.whatsapp_number || ''}
                      onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
                      className={errors.whatsapp_number ? 'border-red-500' : ''}
                    />
                    {errors.whatsapp_number && (
                      <p className="text-sm text-red-500 mt-1">{errors.whatsapp_number}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="phone_id">ID Telefone *</Label>
                    <Input
                      id="phone_id"
                      placeholder="ID do telefone no Facebook"
                      value={formData.phone_id || ''}
                      onChange={(e) => handleInputChange('phone_id', e.target.value)}
                      className={errors.phone_id ? 'border-red-500' : ''}
                    />
                    {errors.phone_id && (
                      <p className="text-sm text-red-500 mt-1">{errors.phone_id}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="business_account_id">ID Conta Comercial *</Label>
                  <Input
                    id="business_account_id"
                    placeholder="ID da conta comercial"
                    value={formData.business_account_id || ''}
                    onChange={(e) => handleInputChange('business_account_id', e.target.value)}
                    className={errors.business_account_id ? 'border-red-500' : ''}
                  />
                  {errors.business_account_id && (
                    <p className="text-sm text-red-500 mt-1">{errors.business_account_id}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="permanent_token">Token Permanente *</Label>
                  <Input
                    id="permanent_token"
                    type="password"
                    placeholder="Token permanente do Facebook"
                    value={formData.permanent_token || ''}
                    onChange={(e) => handleInputChange('permanent_token', e.target.value)}
                    className={errors.permanent_token ? 'border-red-500' : ''}
                  />
                  {errors.permanent_token && (
                    <p className="text-sm text-red-500 mt-1">{errors.permanent_token}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="chatconnect_account_id_oficial">ID da Conta do ChatConnect *</Label>
                    <Input
                      id="chatconnect_account_id_oficial"
                      placeholder="ID da conta ChatConnect"
                      value={formData.chatconnect_account_id || ''}
                      onChange={(e) => handleInputChange('chatconnect_account_id', e.target.value)}
                      className={errors.chatconnect_account_id ? 'border-red-500' : ''}
                    />
                    {errors.chatconnect_account_id && (
                      <p className="text-sm text-red-500 mt-1">{errors.chatconnect_account_id}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="chatconnect_oficial_inbox_id">ID da Caixa de Entrada Oficial *</Label>
                    <Input
                      id="chatconnect_oficial_inbox_id"
                      placeholder="ID da caixa de entrada"
                      value={formData.chatconnect_oficial_inbox_id || ''}
                      onChange={(e) => handleInputChange('chatconnect_oficial_inbox_id', e.target.value)}
                      className={errors.chatconnect_oficial_inbox_id ? 'border-red-500' : ''}
                    />
                    {errors.chatconnect_oficial_inbox_id && (
                      <p className="text-sm text-red-500 mt-1">{errors.chatconnect_oficial_inbox_id}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Status Ativo */}
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active || false}
                onCheckedChange={(checked) => handleInputChange('is_active', checked)}
              />
              <Label htmlFor="is_active">Conexão ativa</Label>
            </div>

            {/* Botões */}
            <div className="flex items-center gap-3 pt-4">
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {editingConnection ? 'Atualizar' : 'Criar'} Conexão
                  </>
                )}
              </Button>
              
              <Button
                variant="outline"
                onClick={handleCancelForm}
                disabled={saving}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}


      {/* Modal de configuração de estratégias */}
      <ApiStrategyModal
        isOpen={showStrategyModal}
        onClose={handleCloseStrategyModal}
        connection={selectedConnectionForStrategy}
        clientId={client.id}
      />

      {/* Modal de ativação da API Meta */}
      <MetaActivationWizard
        isOpen={showMetaActivationWizard}
        onClose={() => setShowMetaActivationWizard(false)}
      />

    </TabsContent>
  );
};

export default ApiTab;
